import { isMobileTouchScreen } from "@utils/deviceCapabilities";
import { isMobileDynamic } from "@utils/isMobileDynamic";
import { waitForElement } from "@utils/waitForElement";
import { dispatchScrollToBottom } from "@atoms/_page-controller/PageController";
import ParallaxWithFadeout from "./ParallaxWithFadeout.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const classes = {
  fullHeight: "full-height",
  fullHeightMobile: "mobile-full-height",
  hasBackgroundVideo: "has-background-video",
  mobileVideoAboveText: "mobile-video-above-text",
  hasMobileViewAbove: "has-mobile-view-above",
  ratio169: "ratio-16-9",
  ratio43: "ratio-4-3",
  ratio11: "ratio-1-1",
  dynamicmedia: "dynamicmedia",
  tallContent: "tall-content",
  tallContentVideo: "tall-content-video",
  differentStyleSchemes: "different-style-schemes",
  videoBasedRatio: "video-based-ratio",
} as const;

const selectors = {
  backgroundDesktop: ":scope > .background.desktop-view",
  backgroundMobile: ":scope > .background.mobile-view",
  overlay: ":scope > .overlay",
  container: ":scope > .container",
  backgroundDesktopMovie: ":scope > .movie > .comp-movie",
  backgroundMobileMovie: ".mobile-view .movie .comp-movie",
  navigationLinksBar: ".enh-nav__links-bar-content",
  navigation: ".comp-main-navigation.comp-enhanced-navigation",
  sectionScrollArrow: "div.section-scroll-arrow > a",
  fixedImage: ".fixed-image",
  navBar: ".comp-nav-bar",
  movieCanvas: ".movie-canvas",
  jsVideoViewer: ".js-video-viewer",
  s7container: ".s7container",
  cqDdVideo: ".cq-dd-video",
  compMovieBgVideo: ".comp-movie.js-is-background-video",
  section: ".comp-section",
  vimeo: ".vimeo",
  youtube: ".youtube",
  background: ".background",
} as const;

const variables = {
  parallaxMaxDistance: 500,
  defaultNavBarHeight: 64,
  aspectRatio169: 16 / 9,
  aspectRatio43: 4 / 3,
  aspectRatio11: 1,
  waypointOffset: -25,
  parallaxSpeed: 0.3,
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseAspectRatio(str: string | null | undefined): number {
  if (!str || typeof str !== "string") return 1;
  str = str.trim().toLowerCase();
  if (["auto", "unset", "initial", "inherit"].includes(str)) return 1;
  if (/^\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?$/.test(str)) {
    const [w, h] = str.split("/").map((s) => Number(s.trim()));
    if (w > 0 && h > 0 && w < 10000 && h < 10000) {
      const ratio = w / h;
      if (ratio > 0.01 && ratio < 100) return ratio;
    }
    return 1;
  }
  const num = Number(str);
  if (!isNaN(num) && num > 0.01 && num < 100) return num;
  return 1;
}

function outerHeight(el: HTMLElement | null): number {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  return (
    el.offsetHeight +
    parseInt(style.marginTop, 10) +
    parseInt(style.marginBottom, 10)
  );
}

// ---------------------------------------------------------------------------
// Section class
// ---------------------------------------------------------------------------

export class Section {
  private ctx: HTMLElement;
  private background: HTMLElement | null;
  private backgroundMobile: HTMLElement | null;
  private overlay: HTMLElement | null;
  private container: HTMLElement | null;
  private backgroundDesktopMovie: HTMLElement | null;
  private backgroundMobileMovie: HTMLElement | null;
  private navigationLinksBar: HTMLElement | null;
  private navigation: HTMLElement | null;
  private styleDesktop: string;
  private styleMobile: string;
  private fade: boolean;
  private hasVideoBasedAspectRatio: boolean;
  private isMobileFullHeightEnabled: boolean;
  private mobileImageAbove: boolean;
  private mobileVideoAboveText: boolean;
  private hasParallax: boolean;
  private parallax: InstanceType<typeof ParallaxWithFadeout> | null = null;
  private video: NodeListOf<HTMLElement> | null = null;
  private videoRatio: number | null = null;
  private sectionRatio: number | null = null;
  private backgroundDimensions: { width: number; height: number; ratio: number } | null = null;
  private fullHeightVideoResizeHandler: (() => void) | null = null;
  private abortController = new AbortController();

  constructor(element: HTMLElement) {
    this.ctx = element;
    this.background = this.ctx.querySelector(selectors.backgroundDesktop);
    this.backgroundMobile = this.ctx.querySelector(selectors.backgroundMobile);
    this.overlay = this.ctx.querySelector(selectors.overlay);
    this.container = this.ctx.querySelector(selectors.container);
    this.backgroundDesktopMovie = this.ctx.querySelector(selectors.backgroundDesktopMovie);
    this.backgroundMobileMovie = this.ctx.querySelector(selectors.backgroundMobileMovie);
    this.navigationLinksBar = document.body.querySelector(selectors.navigationLinksBar);
    this.navigation = document.body.querySelector(selectors.navigation);
    this.styleDesktop = this.ctx.dataset.styleDesktop ?? "";
    this.styleMobile = this.ctx.dataset.styleMobile ?? "";
    this.fade = this.ctx.dataset.fadeOut === "true";
    this.hasVideoBasedAspectRatio = this.ctx.classList.contains(classes.videoBasedRatio);
    this.isMobileFullHeightEnabled = this.ctx.classList.contains(classes.fullHeightMobile);
    this.mobileImageAbove = this.ctx.classList.contains(classes.hasMobileViewAbove);
    this.mobileVideoAboveText = this.ctx.classList.contains(classes.mobileVideoAboveText);
    this.hasParallax = this.ctx.classList.contains("has-parallax");
  }

  init(): this {
    const { signal } = this.abortController;

    if (this.ctx.classList.contains(classes.differentStyleSchemes)) {
      this.changeStyleSchema();
      document.addEventListener("viewportChange", this.changeStyleSchema.bind(this), { signal });
    }

    if (this.ctx.classList.contains(classes.fullHeight)) {
      document.addEventListener("viewportChange", this.initFullHeight.bind(this), { signal });
      this.initFullHeight();
    }

    this.mobileImageAbove = this.ctx.classList.contains(classes.hasMobileViewAbove);
    this.mobileVideoAboveText = this.ctx.classList.contains(classes.mobileVideoAboveText);
    this.initBackgroundProperties();

    if (
      this.ctx.classList.contains(classes.hasBackgroundVideo) ||
      this.ctx.classList.contains(classes.mobileVideoAboveText)
    ) {
      this.video = null;
      this.videoRatio = null;
      this.sectionRatio = null;
      this.initBackgroundVideo();
    }

    if (this.ctx.dataset.parallaxDirection && !isMobileTouchScreen()) {
      const parallaxMaxDistance = variables.parallaxMaxDistance;
      const parallaxDistance = parallaxMaxDistance * variables.parallaxSpeed;
      const parallaxDirection = this.ctx.dataset.parallaxDirection;

      if (this.background) {
        this.initBackgroundDimensions(this.background, parallaxDirection, parallaxDistance);
        this.initParallax(this.background, parallaxDirection, parallaxDistance);
        document.addEventListener(
          "viewportChange",
          () => this.initParallax(this.background!, parallaxDirection, parallaxDistance),
          { signal },
        );
        window.addEventListener(
          "resize",
          () => this.onRatioChanged(this.background!, parallaxDirection, parallaxDistance),
          { signal },
        );
      }
    }

    this.ctx
      .querySelector(selectors.sectionScrollArrow)
      ?.addEventListener("click", this.onSectionScrollArrowClick.bind(this), { signal });

    if (!this.isEditMode()) {
      this.applyFixedImageMargin();
      document.addEventListener("viewportChange", this.applyFixedImageMargin.bind(this), { signal });
    }

    if (this.hasVideoBasedAspectRatio) {
      document.addEventListener("viewportChange", this.handleAspectRatio.bind(this), { signal });
      this.handleAspectRatio();
    }

    return this;
  }

  destroy(): void {
    this.abortController.abort();
    this.parallax?.destroy();
  }

  // ---------------------------------------------------------------------------
  // Private methods
  // ---------------------------------------------------------------------------

  private isEditMode(): boolean {
    return window.top?.location.pathname.startsWith("/editor.html") ?? false;
  }

  private applyFixedImageMargin(): void {
    const fixedImage = this.ctx.querySelector<HTMLElement>(selectors.fixedImage);
    if (!fixedImage) return;
    if ([...fixedImage.classList].some((c) => c.startsWith("top-"))) {
      const navBar = document.querySelector<HTMLElement>(selectors.navBar);
      const img = fixedImage.querySelector<HTMLElement>("img");
      if (img) img.style.top = `${navBar?.offsetHeight ?? 0}px`;
    }
  }

  private initFullHeight(): void {
    const isDesktop = !document.documentElement.className.includes("small");
    const navBar = document.querySelector<HTMLElement>(selectors.navBar);
    const headerHeight = navBar?.offsetHeight ?? 0;
    const minHeight = `calc(100vh - ${headerHeight}px)`;
    const isDesktopOrMobileFullHeight = isDesktop || this.isMobileFullHeightEnabled;

    this.ctx.style.minHeight = isDesktopOrMobileFullHeight ? minHeight : "unset";

    if (this.ctx.classList.contains(classes.hasBackgroundVideo)) {
      if (isDesktop && this.backgroundDesktopMovie) {
        const movieWrapper = this.backgroundDesktopMovie.querySelector<HTMLElement>(".movie-wrapper");
        const aspectRatioStr = movieWrapper
          ? window.getComputedStyle(movieWrapper).aspectRatio
          : null;
        this.runBackgroundVideoAdjustment(parseAspectRatio(aspectRatioStr));
      } else if (this.isMobileFullHeightEnabled && this.backgroundMobileMovie) {
        const movieWrapper = this.backgroundMobileMovie.querySelector<HTMLElement>(".movie-wrapper");
        const aspectRatioStrMobile = movieWrapper
          ? window.getComputedStyle(movieWrapper).aspectRatio
          : null;
        this.runBackgroundVideoAdjustment(parseAspectRatio(aspectRatioStrMobile));
      }
    }
  }

  private runBackgroundVideoAdjustment(aspectratio: number): void {
    const tId = this.ctx.dataset.tId;

    if (this.backgroundDesktopMovie?.classList.contains(classes.dynamicmedia)) {
      const videoSelector = `.comp-section[data-t-id="${tId}"] > .movie video`;
      waitForElement(videoSelector).then(() => this.fullHeightVideoBackground(aspectratio));
    } else if (
      this.backgroundDesktopMovie?.matches(`${selectors.vimeo}, ${selectors.youtube}`)
    ) {
      this.fullHeightVideoBackground(aspectratio);
    }

    if (this.backgroundMobileMovie?.classList.contains(classes.dynamicmedia)) {
      const videoSelector = `.comp-section[data-t-id="${tId}"] .background .movie video`;
      waitForElement(videoSelector).then(() => this.fullHeightVideoBackground(aspectratio));
    } else if (
      this.backgroundMobileMovie?.matches(`${selectors.vimeo}, ${selectors.youtube}`)
    ) {
      this.fullHeightVideoBackground(aspectratio);
    }
  }

  private fullHeightVideoBackground(aspectratio: number): void {
    const navHeight = outerHeight(this.navigation);
    const navBarHeight = navHeight || variables.defaultNavBarHeight;
    let windowRatio = window.innerWidth / (window.innerHeight - navBarHeight);
    let sectionRatio = window.innerWidth / (this.ctx.parentElement?.offsetHeight ?? 1);

    if (windowRatio < aspectratio && sectionRatio < aspectratio) {
      this.ctx.classList.add(classes.tallContent);
      this.applyBackgroundVideoSectionWidth();
    }

    if (this.fullHeightVideoResizeHandler) {
      window.removeEventListener("resize", this.fullHeightVideoResizeHandler);
    }

    const resizeHandler = () => {
       windowRatio = window.innerWidth / (window.innerHeight - navBarHeight);
       sectionRatio = window.innerWidth / (this.ctx.parentElement?.offsetHeight ?? 1);
       if (windowRatio < aspectratio && sectionRatio < aspectratio) {
         this.ctx.classList.add(classes.tallContent);
         this.applyBackgroundVideoSectionWidth();
       } else {
         this.ctx.classList.remove(classes.tallContent);
         this.resetBackgroundVideoSectionWidth();
       }
     };
      this.fullHeightVideoResizeHandler = resizeHandler;
      window.addEventListener("resize", this.fullHeightVideoResizeHandler, { signal: this.abortController.signal });
  }

  private applyBackgroundVideoSectionWidth(): void {
    const sectionHeight = this.ctx.parentElement?.offsetHeight ?? 0;
    const videoSelectors = `${selectors.movieCanvas}, ${selectors.jsVideoViewer}, ${selectors.s7container}, ${selectors.cqDdVideo}`;

    if (this.backgroundDesktopMovie) {
      const videoWidth = this.getRatio(this.backgroundDesktopMovie) * sectionHeight;
      this.backgroundDesktopMovie
        .querySelectorAll<HTMLElement>(videoSelectors)
        .forEach((el) => (el.style.width = `${videoWidth}px`));
    }

    if (
      this.backgroundMobileMovie &&
      !(
        this.mobileVideoAboveText &&
        this.backgroundMobileMovie.classList.contains(classes.dynamicmedia)
      ) &&
      this.isMobileFullHeightEnabled
    ) {
      const videoWidthMobile = this.getRatio(this.backgroundMobileMovie) * sectionHeight;
      this.backgroundMobileMovie
        .querySelectorAll<HTMLElement>(videoSelectors)
        .forEach((el) => (el.style.width = `${videoWidthMobile}px`));
    }
  }

  private getRatio(movie: HTMLElement): number {
    if (movie.classList.contains(classes.ratio169)) return variables.aspectRatio169;
    if (movie.classList.contains(classes.ratio43)) return variables.aspectRatio43;
    return variables.aspectRatio11;
  }

  private resetBackgroundVideoSectionWidth(): void {
    const videoSelectors = `${selectors.movieCanvas}, ${selectors.jsVideoViewer}, ${selectors.s7container}, ${selectors.cqDdVideo}`;
    this.backgroundDesktopMovie
      ?.querySelectorAll<HTMLElement>(videoSelectors)
      .forEach((el) => (el.style.width = ""));
  }

  private onSectionScrollArrowClick(event: Event): void {
    event.preventDefault();
    const section = (event.target as HTMLElement).closest<HTMLElement>(selectors.section);
    if (section) dispatchScrollToBottom(section);
  }

  private initParallax(background: HTMLElement, direction: string, distance: number): void {
    if (this.isEditMode()) return;

    const opacityDesktop = this.ctx.dataset.overlayOpacity;
    const linksBarHeight = outerHeight(this.navigationLinksBar);
    const headerOffset = linksBarHeight || 64;

    if (this.hasParallax) {
      background.style.backgroundAttachment = "fixed";
      if (direction === "fixed") {
        background.style.backgroundPosition = `50% calc(50% + ${headerOffset}px)`;
      }
    }

    this.parallax?.destroy();
    this.parallax = new ParallaxWithFadeout(background, {
      direction,
      distance,
      headerOffset,
      fade: this.fade,
      opacityApplied: !!opacityDesktop,
      hasParallax: this.hasParallax,
    });
  }

  private initBackgroundDimensions(background: HTMLElement, direction: string, distance: number): void {
    const bgImage = window.getComputedStyle(background).backgroundImage;
    const imageSrc = bgImage.replace(/url\((['"])?(.*?)\1\)/gi, "$2").split(",")[0];

    if (imageSrc && imageSrc !== "none") {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        this.backgroundDimensions = {
          width: image.width,
          height: image.height,
          ratio: image.width / image.height,
        };
        this.onRatioChanged(background, direction, distance);
      };
    }
  }

  private onRatioChanged(background: HTMLElement, direction: string, distance: number): void {
    if (!this.backgroundDimensions || this.isEditMode()) return;

    const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    if (direction === "left" || direction === "right") {
      const threshold = (vw + distance) / this.backgroundDimensions.ratio;
      background.style.backgroundSize =
        vh >= threshold ? "auto calc(100vh)" : `calc(100vw + ${distance}px) auto`;
    } else if (direction === "up" || direction === "down") {
      const threshold = vw / this.backgroundDimensions.ratio - distance;
      background.style.backgroundSize =
        vh >= threshold ? `auto calc(100vh + ${distance}px)` : "calc(100vw) auto";
    }
  }

  private initBackgroundVideo(): void {
    this.video = this.ctx.querySelectorAll<HTMLElement>(selectors.compMovieBgVideo);
    const videoClasses = this.video[0]?.className ?? "";

    if (videoClasses.includes(classes.ratio169)) {
      this.videoRatio = variables.aspectRatio169;
    } else if (videoClasses.includes(classes.ratio43)) {
      this.videoRatio = variables.aspectRatio43;
    } else {
      this.videoRatio = variables.aspectRatio11;
    }

    if (!this.ctx.classList.contains(classes.fullHeight) || this.mobileVideoAboveText) {
      this.repositionBackgroundVideo();
      window.addEventListener("resize", this.repositionBackgroundVideo.bind(this), {
        signal: this.abortController.signal,
      });
    }
  }

  private updateMovieWrapperStyles(movieWrapper: HTMLElement): void {
    if (
      !movieWrapper.closest(selectors.background) &&
      this.video?.[0] &&
      !this.video[0].classList.contains(classes.videoBasedRatio)
    ) {
      movieWrapper.style.minHeight = `${this.ctx.offsetHeight}px`;
    }
  }

  private repositionBackgroundVideo(): void {
    const sw = this.ctx.offsetWidth;
    const sh = this.ctx.offsetHeight;
    this.sectionRatio = sw / sh;
    const isMobileScreen = isMobileDynamic();

    let containerPaddingTop = 0;
    const hasBgVideo = this.ctx.classList.contains(classes.hasBackgroundVideo);
    const mobileScreenVideoChecks =
      isMobileScreen && this.mobileVideoAboveText && !this.mobileImageAbove;

    if (!this.video) return;

    if (hasBgVideo && mobileScreenVideoChecks) {
      const mobileVideo = this.video[1] as HTMLElement | undefined;
      containerPaddingTop = mobileVideo?.offsetHeight ?? 0;
      Array.from(this.video).forEach((v) => {
        v.style.width = "";
        v.style.height = "";
      });
    } else if (mobileScreenVideoChecks) {
      const mobileVideo = this.video[0];
      containerPaddingTop = mobileVideo?.offsetHeight ?? 0;
      Array.from(this.video).forEach((v) => {
        v.style.width = "";
        v.style.height = "";
      });
    } else if (!isMobileScreen) {
      const [w, h] =
        this.sectionRatio >= (this.videoRatio ?? 1) ? ["auto", "100%"] : ["100%", "auto"];
      Array.from(this.video).forEach((v) => {
        v.style.width = w;
        v.style.height = h;
      });
    } else {
      Array.from(this.video).forEach((v) => {
        v.style.width = "100%";
        v.style.height = "100%";
      });
    }

    this.video[0]
      ?.querySelectorAll<HTMLElement>(":scope > .movie-wrapper")
      .forEach((mw) => this.updateMovieWrapperStyles(mw));

    if (!this.mobileImageAbove && this.container) {
      this.container.style.paddingTop = `${containerPaddingTop}px`;
    }
  }

  private updateOpacity(): void {
    if (!this.overlay) return;
    const opacityDesktop = this.ctx.dataset.overlayOpacity;
    const opacityMobile = this.ctx.dataset.overlayOpacityMobile;
    const isMobileScreen = isMobileDynamic();

    if (isMobileScreen) {
      const backgroundAboveContent = this.mobileVideoAboveText || this.mobileImageAbove;
      this.overlay.style.opacity = backgroundAboveContent ? "0" : (opacityMobile ?? "0");
    } else {
      this.overlay.style.opacity = opacityDesktop ?? "0";
    }
  }

  private hasRenderedBackgroundImage(background: HTMLElement | null): boolean {
    if (!background) return false;
    const image = window.getComputedStyle(background).backgroundImage;
    return Boolean(image && image !== "none");
  }

  private syncBackgroundClasses(): void {
    const isSmall = document.documentElement.className.includes("small");
    const hasDesktopBackgroundImage = this.hasRenderedBackgroundImage(this.background);
    const hasMobileBackgroundImage = this.hasRenderedBackgroundImage(this.backgroundMobile);

    // On mobile, prioritize mobile background, but keep desktop as fallback.
    const hasBackgroundImage = isSmall
      ? hasMobileBackgroundImage || hasDesktopBackgroundImage
      : hasDesktopBackgroundImage || hasMobileBackgroundImage;

    this.ctx.classList.toggle("has-background-image", hasBackgroundImage);

    const datasetBackgroundColor = this.ctx.dataset.backgroundColor;
    const inlineBackgroundColor = this.ctx.style.backgroundColor;
    const hasBackgroundColor = Boolean(datasetBackgroundColor || inlineBackgroundColor);

    this.ctx.classList.toggle("has-background-color", hasBackgroundColor);

    if (!inlineBackgroundColor && datasetBackgroundColor) {
      this.ctx.style.backgroundColor = datasetBackgroundColor;
    }
  }

  private initBackgroundProperties(): void {
    // Keep frontend parity with the old TerrificJS behavior as a fallback.
    // Backend is expected to emit these classes, but we normalize here in case
    // of mixed markup during rollout.
    this.syncBackgroundClasses();
    document.addEventListener("viewportChange", this.syncBackgroundClasses.bind(this), {
      signal: this.abortController.signal,
    });

    if (!this.fade) {
      this.updateOpacity();
      document.addEventListener("viewportChange", this.updateOpacity.bind(this), {
        signal: this.abortController.signal,
      });
    }
  }

  private changeStyleSchema(): void {
    const isSmall = document.documentElement.className.includes("small");

    if (isSmall) {
      this.ctx.classList.remove("different-style-schemes", `style-${this.styleDesktop}`);
      this.ctx.classList.add(`style-${this.styleMobile}`);
    } else {
      this.ctx.classList.remove("different-style-schemes", `style-${this.styleMobile}`);
      this.ctx.classList.add(`style-${this.styleDesktop}`);
    }
  }

  private handleAspectRatio(): void {
    if (!this.backgroundDesktopMovie || !this.backgroundMobileMovie) return;

    const isSmall = document.documentElement.className.includes("small");
    const movieWrapper = isSmall
      ? this.backgroundMobileMovie.querySelector<HTMLElement>(".movie-wrapper")
      : this.backgroundDesktopMovie.querySelector<HTMLElement>(".movie-wrapper");

    const backgroundMovieHeight = movieWrapper?.offsetHeight ?? 0;

    if ((this.container?.offsetHeight ?? 0) > backgroundMovieHeight) {
      this.ctx.classList.add(classes.tallContentVideo);
    } else {
      this.ctx.classList.remove(classes.tallContentVideo);
    }
  }

  // ---------------------------------------------------------------------------
  // Static bootstrap
  // ---------------------------------------------------------------------------

  static initAll(): void {
    document
      .querySelectorAll<HTMLElement>('[data-component="section"]')
      .forEach((el) => new Section(el).init());
  }
}
